/**
 * Day 2, what the evidence says: the readiness decision with its reasons,
 * and the normalized observations grouped by type, each with its source,
 * basis, confidence and location, and the original text when it adds
 * something.
 */

import type { ReactNode } from 'react';

import type { AnalysisDay2State, AnalysisEvidenceObservation } from '../../api.js';
import { EmptyState, Tag } from '../../ui.js';
import { SectionHeading } from '../common.js';
import type { AnalysisRunPageCopy } from './run-page-copy.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';

function evidenceLabel(observation: AnalysisEvidenceObservation): string {
  const support = [observation.basis, observation.confidence]
    .filter((entry): entry is string => entry !== null)
    .join(' · ');
  return `${observation.sourcePlatform} · ${support}`;
}

export function ReadinessAndEvidenceSection({
  day2,
  copy,
}: {
  readonly day2: AnalysisDay2State;
  readonly copy: AnalysisRunPageCopy;
}): ReactNode {
  const observationsByType = new Map<string, AnalysisEvidenceObservation[]>();
  for (const observation of day2.observations) {
    const entries = observationsByType.get(observation.type) ?? [];
    entries.push(observation);
    observationsByType.set(observation.type, entries);
  }

  return (
    <>
      <section id="analysis-readiness" style={{ marginBottom: 'var(--space-lg)' }}>
        <SectionHeading>{copy.readiness}</SectionHeading>
        {day2.readiness === null ? (
          <EmptyState title={copy.noEvidence} compact />
        ) : (
          <div style={card}>
            <Tag tone={toneFor(day2.readiness.state)}>{day2.readiness.state}</Tag>
            <ul>
              {day2.readiness.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section id="normalized-evidence" style={{ marginBottom: 'var(--space-lg)' }}>
        <SectionHeading>{copy.evidence}</SectionHeading>
        {day2.observations.length === 0 ? (
          <EmptyState title={copy.noEvidence} compact />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {[...observationsByType.entries()].map(([type, observations]) => (
              <article key={type} style={card}>
                <h3 style={{ font: 'var(--type-heading-sm)' }}>
                  {type.replaceAll('_', ' ')}
                </h3>
                {observations.map((observation) => (
                  <div
                    key={observation.id}
                    style={{
                      borderTop: '1px solid var(--border-hairline)',
                      padding: '10px 0',
                    }}
                  >
                    <strong>{observation.value}</strong>
                    <p
                      style={{
                        font: 'var(--type-caption)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {evidenceLabel(observation)} ·{' '}
                      {observation.evidenceLocation ?? 'location unavailable'}
                    </p>
                    {observation.originalText &&
                    observation.originalText !== observation.value ? (
                      <details>
                        <summary>Supporting source text</summary>
                        <blockquote dir="auto">{observation.originalText}</blockquote>
                      </details>
                    ) : null}
                  </div>
                ))}
              </article>
            ))}
          </div>
        )}
      </section>
    </>
  );
}
